import { existsSync, readFileSync } from "node:fs";
import { dirname, relative, resolve } from "node:path";
import { visit } from "unist-util-visit";
import { parseMarkdown, getTextContent } from "../../markdown.js";
import type { DriftIssue } from "../../types.js";

/**
 * Validate intra-repo markdown links — both the target file and, when present,
 * the heading anchor. Deterministic, no network: external URLs are skipped.
 *
 * This widens the moat: no surveyed competitor link-checks *instruction* files,
 * and dead links/anchors in ROUTER.md or AGENTS.md silently misroute agents.
 */
export function checkLinks(scaffoldFiles: string[], projectRoot: string): DriftIssue[] {
  const issues: DriftIssue[] = [];
  // Cache slugified headings per target file so a doc linked many times is parsed once.
  const headingCache = new Map<string, Set<string> | null>();

  for (const filePath of scaffoldFiles) {
    if (!existsSync(filePath)) continue;
    const source = relative(projectRoot, filePath);
    let content: string;
    try {
      content = readFileSync(filePath, "utf-8");
    } catch {
      continue;
    }

    const tree = parseMarkdown(content);
    visit(tree, "link", (node: { url: string; position?: { start: { line: number } } }) => {
      const { target, anchor } = splitLink(node.url);
      if (target === null) return; // external, mailto, or pure same-file anchor handled below
      const line = node.position?.start.line ?? null;

      // Pure in-file anchor link: [x](#section)
      if (target === "" && anchor) {
        const slugs = getHeadingSlugs(filePath, headingCache, content);
        if (slugs && !slugs.has(anchor)) {
          issues.push(deadAnchor(source, line, node.url, anchor, "this file"));
        }
        return;
      }

      const targetPath = resolve(dirname(filePath), target);
      if (!existsSync(targetPath)) {
        issues.push({
          code: "DEAD_LINK",
          severity: "warning",
          file: source,
          line,
          message: `Link target ${target} does not exist (referenced as ${node.url})`,
          explanation:
            "A relative markdown link points to a file that is missing on disk, so agents following it hit a dead end.",
          suggestion: `Fix or remove the link to \`${target}\` in ${source}.`,
        });
        return;
      }

      if (anchor) {
        const slugs = getHeadingSlugs(targetPath, headingCache);
        if (slugs && !slugs.has(anchor)) {
          issues.push(deadAnchor(source, line, node.url, anchor, target));
        }
      }
    });
  }

  return issues;
}

function deadAnchor(
  source: string,
  line: number | null,
  url: string,
  anchor: string,
  targetLabel: string
): DriftIssue {
  return {
    code: "DEAD_ANCHOR",
    severity: "warning",
    file: source,
    line,
    message: `Link anchor #${anchor} has no matching heading in ${targetLabel} (referenced as ${url})`,
    explanation:
      "The link resolves to a real file but no heading slugifies to the anchor, so the jump lands nowhere.",
    suggestion: `Update the #${anchor} fragment to match a real heading, or remove it.`,
  };
}

/**
 * Split a markdown link URL into a repo-relative file target and an anchor.
 * Returns target=null for links we deliberately do not check (external,
 * absolute, mailto, protocol-relative).
 */
function splitLink(url: string): { target: string | null; anchor: string | null } {
  // Skip external / protocol / mailto / absolute links — no network, no FS-root assumptions.
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith("//") || url.startsWith("/")) {
    return { target: null, anchor: null };
  }
  const hashIndex = url.indexOf("#");
  if (hashIndex === -1) {
    return isMarkdownish(url) ? { target: url, anchor: null } : { target: null, anchor: null };
  }
  const path = url.slice(0, hashIndex);
  const anchor = decodeURIComponent(url.slice(hashIndex + 1)).toLowerCase();
  if (path === "") return { target: "", anchor: anchor || null };
  return isMarkdownish(path) ? { target: path, anchor: anchor || null } : { target: null, anchor: null };
}

/** Only check links to local docs (markdown or extensionless), not arbitrary assets. */
function isMarkdownish(path: string): boolean {
  return path.endsWith(".md") || path.endsWith(".markdown") || !/\.[a-z0-9]+$/i.test(path);
}

function getHeadingSlugs(
  filePath: string,
  cache: Map<string, Set<string> | null>,
  knownContent?: string
): Set<string> | null {
  const cached = cache.get(filePath);
  if (cached !== undefined) return cached;

  let content = knownContent;
  if (content === undefined) {
    if (!filePath.endsWith(".md") && !filePath.endsWith(".markdown")) {
      cache.set(filePath, null); // can't extract headings from a non-markdown target
      return null;
    }
    try {
      content = readFileSync(filePath, "utf-8");
    } catch {
      cache.set(filePath, null);
      return null;
    }
  }

  const slugs = new Set<string>();
  const seen = new Map<string, number>();
  const tree = parseMarkdown(content);
  visit(tree, "heading", (node) => {
    slugs.add(dedupeSlug(slugify(getTextContent(node)), seen));
  });
  cache.set(filePath, slugs);
  return slugs;
}

/**
 * GitHub-flavored heading slug: lowercase, strip punctuation, spaces → hyphens.
 * Unicode-aware so non-ASCII headings (e.g. "Übersicht") slug correctly.
 */
function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, "")
    .replace(/\s+/g, "-");
}

/** GitHub appends -1, -2, … to repeated slugs within a document. */
function dedupeSlug(slug: string, seen: Map<string, number>): string {
  const count = seen.get(slug);
  if (count === undefined) {
    seen.set(slug, 0);
    return slug;
  }
  const next = count + 1;
  seen.set(slug, next);
  return `${slug}-${next}`;
}
